export type DiffStatus = 'equal' | 'changed' | 'left-only' | 'right-only'

export interface DiffNode {
  readonly status: DiffStatus
  readonly hasDifferences: boolean
  readonly leftValue?: unknown
  readonly rightValue?: unknown
  readonly children: Record<string, DiffNode>
}

export type PickSource = 'left' | 'right'
export interface MergeSelectionMap {
  [key: string]: MergeSelection
}
export type MergeSelection = PickSource | MergeSelectionMap

function isPlainObject(val: unknown): val is Record<string, unknown> {
  return val !== null && typeof val === 'object' && !Array.isArray(val)
}

// Pretty-print a config leaf value for a diff cell. Objects/arrays render as
// multi-line indented JSON (shown in a pre-wrap cell); scalars render plainly.
export function formatConfigValue(value: unknown): string {
  if (typeof value === 'string') return value
  if (value === undefined) return '—'
  if (value === null || typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value, null, 2)
}

export function computeDiff(left: Record<string, unknown>, right: Record<string, unknown>): DiffNode {
  const allKeys = new Set([...Object.keys(left), ...Object.keys(right)])
  const children: Record<string, DiffNode> = {}
  let hasDifferences = false

  for (const key of allKeys) {
    const inLeft = key in left
    const inRight = key in right
    const lVal = left[key]
    const rVal = right[key]

    if (inLeft && !inRight) {
      children[key] = { status: 'left-only', hasDifferences: true, leftValue: lVal, children: {} }
      hasDifferences = true
    } else if (!inLeft && inRight) {
      children[key] = { status: 'right-only', hasDifferences: true, rightValue: rVal, children: {} }
      hasDifferences = true
    } else if (isPlainObject(lVal) && isPlainObject(rVal)) {
      const nested = computeDiff(lVal, rVal)
      children[key] = nested
      if (nested.hasDifferences) hasDifferences = true
    } else if (JSON.stringify(lVal) === JSON.stringify(rVal)) {
      children[key] = { status: 'equal', hasDifferences: false, leftValue: lVal, rightValue: rVal, children: {} }
    } else {
      children[key] = { status: 'changed', hasDifferences: true, leftValue: lVal, rightValue: rVal, children: {} }
      hasDifferences = true
    }
  }

  return { status: hasDifferences ? 'changed' : 'equal', hasDifferences, children }
}

export function mergeSections(
  left: Record<string, unknown>,
  right: Record<string, unknown>,
  selections: Record<string, MergeSelection>,
): Record<string, unknown> {
  const result: Record<string, unknown> = {}

  for (const [key, sel] of Object.entries(selections)) {
    if (sel === 'left') {
      if (key in left) result[key] = left[key]
    } else if (sel === 'right') {
      if (key in right) result[key] = right[key]
    } else if (isPlainObject(sel)) {
      const lChild = isPlainObject(left[key]) ? (left[key] as Record<string, unknown>) : {}
      const rChild = isPlainObject(right[key]) ? (right[key] as Record<string, unknown>) : {}
      result[key] = mergeSections(lChild, rChild, sel as Record<string, MergeSelection>)
    }
  }

  return result
}
